import { InstallmentCalculator, PdlCalculator } from "@libs/kz-loan-calc";
import CalcUtilities from "./CalcUtilities";
import CalcSlider from "./CalcSlider";
import {
  CALC_SLIDER_RAIL_SELECTOR,
  PERIOD_SELECT_SELECTOR,
  REPAYMENT_SELECT_SELECTOR,
  MONTHLY_PAYMENT_ITEM_SELECTOR,
  END_OF_TERM_OPTION_SELECTOR,
  DEFAULT_INSTALLMENT_RATE,
  PERIOD_MAPPING,
  CALC_TYPE_ATTRIBUTE,
} from "./constants";

class LoanCalculator {
  constructor() {
    this.calcContainer = document.querySelector(".calc");
    this.calcSliderRail = document.querySelector(CALC_SLIDER_RAIL_SELECTOR);
    this.periodSelect = document.querySelector(PERIOD_SELECT_SELECTOR);
    this.repaymentSelect = document.querySelector(REPAYMENT_SELECT_SELECTOR);
    this.monthlyPaymentItem = document.querySelector(
      MONTHLY_PAYMENT_ITEM_SELECTOR,
    );
    this.endOfTermOption = this.repaymentSelect?.querySelector(
      END_OF_TERM_OPTION_SELECTOR,
    );

    // Определяем тип калькулятора из data-атрибута
    this.calcType = this.calcContainer?.getAttribute(CALC_TYPE_ATTRIBUTE) || null;

    this.calcSlider = null;

    this.initialize();
  }

  initialize() {
    // Скрываем "Ежемесячный платеж" для PDL калькулятора
    if (this.calcType === "pdl" && this.monthlyPaymentItem) {
      const parentLi = this.monthlyPaymentItem.closest(".calc__result-item");
      if (parentLi) {
        parentLi.style.display = "none";
      }
    }

    if (this.calcSliderRail) {
      const initialPeriod = this.periodSelect?.value;
      const initialRange = initialPeriod
        ? CalcUtilities.getLoanRangeByPeriod(initialPeriod)
        : this.calcType === "pdl"
          ? { min: 10_000, max: 300_000 }
          : this.calcType === "installment"
            ? { min: 50_000, max: 700_000 }
            : { min: 50_000, max: 7_000_000 };

      // Определяем начальное значение слайдера
      let startValue;
      if (this.calcType === "pdl") {
        startValue = 100_000; // Для PDL начальное значение 100к
      } else if (this.calcType === "installment") {
        startValue = 300_000; // Для INSTALLMENT начальное значение 300к
      } else {
        startValue = 300_000; // Для общего калькулятора
      }
      // Убеждаемся, что начальное значение в пределах диапазона
      startValue = Math.max(initialRange.min, Math.min(startValue, initialRange.max));

      this.calcSlider = new CalcSlider(
        this.calcSliderRail,
        this.calculateLoan.bind(this),
      );
      this.calcSlider.initialize(initialRange.min, initialRange.max, startValue);
    }

    if (this.periodSelect) {
      this.periodSelect.addEventListener(
        "change",
        this.handlePeriodChange.bind(this),
      );
    }

    if (this.repaymentSelect) {
      this.repaymentSelect.addEventListener(
        "change",
        this.calculateLoan.bind(this),
      );
    }

    if (this.periodSelect?.value) {
      this.calculateLoan();
    }
  }

  getCurrentLoanAmount() {
    return this.calcSlider ? this.calcSlider.getValue() : 0;
  }

  getCurrentPeriod() {
    return this.periodSelect.value;
  }

  getCurrentRepaymentMethod() {
    return this.repaymentSelect.value;
  }

  toggleUIElements(isPdl) {
    // Скрываем "Ежемесячный платеж" для PDL
    if (this.monthlyPaymentItem) {
      const parentLi = this.monthlyPaymentItem.closest(".calc__result-item");
      if (parentLi) {
        // Если это PDL калькулятор, всегда скрываем
        if (this.calcType === "pdl") {
          parentLi.style.display = "none";
        } else {
          parentLi.style.display = isPdl ? "none" : "flex";
        }
      }
    }

    if (this.endOfTermOption) {
      if (isPdl) {
        this.endOfTermOption.style.display = "block";
        this.endOfTermOption.disabled = false;
      } else {
        this.endOfTermOption.style.display = "none";
        this.endOfTermOption.disabled = true;
        if (this.repaymentSelect.value === "end_of_term") {
          this.repaymentSelect.value = "annuity";
        }
      }
    }
  }

  calculatePdl(amount, days) {
    const dailyRate = CalcUtilities.getPdlDailyRate();

    const result = PdlCalculator.calculate({
      amount,
      period: days,
      openDate: CalcUtilities.getCurrentDate(),
      interests: [{ fromDay: 0, toDay: days, rate: dailyRate }],
      discount: 0,
    });

    return {
      returnAmount: result.amountToReturn,
      payment: result.amountToReturn,
      percent: result.annualRate,
      gesv: result.effectiveAnnualRate,
    };
  }

  calculateInstallment(amount, months, repaymentMethod) {
    const result = InstallmentCalculator.calculate({
      amount,
      periods: months,
      openDate: CalcUtilities.getCurrentDate(),
      tariffType: "BASIC_MONTHLY",
      interest: DEFAULT_INSTALLMENT_RATE,
    });

    const isAnnuity = repaymentMethod === "annuity";
    const calculation = isAnnuity
      ? result.annuityCalculation
      : result.differentialCalculation;

    const payment = isAnnuity
      ? calculation.annuityPayment
      : calculation.paymentSchedule[0].payment;

    return {
      returnAmount: calculation.totalPayment,
      payment,
      percent: DEFAULT_INSTALLMENT_RATE,
      gesv: calculation.effectiveAnnualRate,
    };
  }

  updateResultValue(resultType, value) {
    const element = document.querySelector(`[data-result="${resultType}"]`);
    if (element) {
      element.innerHTML = value;
    }
  }

  calculateLoan() {
    const amount = this.getCurrentLoanAmount();
    const periodValue = this.getCurrentPeriod();
    const repaymentMethod = this.getCurrentRepaymentMethod();

    if (!periodValue) {
      console.warn("Период не выбран");
      return;
    }

    const periodData = PERIOD_MAPPING[periodValue];
    if (!periodData) {
      console.warn("Неизвестный период:", periodValue);
      return;
    }

    const isPdl = periodData.isDays;
    this.toggleUIElements(isPdl);

    let results;
    try {
      if (isPdl) {
        results = this.calculatePdl(amount, periodData.days);
      } else {
        results = this.calculateInstallment(
          amount,
          periodData.months,
          repaymentMethod,
        );
      }

      this.updateResultValue(
        "return-amount",
        `${CalcUtilities.formatMoney(results.returnAmount)}&nbsp;₸`,
      );
      this.updateResultValue(
        "monthly-payment",
        `${CalcUtilities.formatMoney(results.payment)}&nbsp;₸`,
      );
      this.updateResultValue(
        "rate",
        `${CalcUtilities.formatPercent(results.percent)}%&nbsp;годовых`,
      );
      this.updateResultValue(
        "gesv",
        `${CalcUtilities.formatPercent(results.gesv)}%`,
      );
    } catch (error) {
      console.error("Ошибка при расчете кредита:", error);
    }
  }

  handlePeriodChange() {
    const newPeriod = this.periodSelect.value;
    const newRange = CalcUtilities.getLoanRangeByPeriod(newPeriod);
    if (this.calcSlider) {
      this.calcSlider.updateRange(newRange.min, newRange.max);
    }
    // Пересчитываем после изменения периода
    this.calculateLoan();
  }
}

new LoanCalculator();
